import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  LineChart, Line, PieChart, Pie, Cell, Legend, ScatterChart, Scatter, ZAxis,
} from "recharts";
import { AlertTriangle } from "lucide-react";

// Illustrative data (clearly labeled as DEMO).
const QUOTE_COMPARE = [
  { supplier: "Shenzhen PackPro",   price: 5820, lead: 22, country: "CN" },
  { supplier: "Berlin Precision",   price: 7100, lead: 25, country: "DE" },
  { supplier: "Osaka Industrial",   price: 6450, lead: 30, country: "JP" },
  { supplier: "Chicago Industrial", price: 7980, lead: 14, country: "US" },
  { supplier: "Rotterdam Logistics",price: 6150, lead: 10, country: "NL" },
];
const RESPONSE_ACTIVITY = [
  { day: "Mon", responses: 4 }, { day: "Tue", responses: 7 }, { day: "Wed", responses: 5 },
  { day: "Thu", responses: 9 }, { day: "Fri", responses: 11 }, { day: "Sat", responses: 3 }, { day: "Sun", responses: 2 },
];
const STATUS = [
  { name: "Draft", value: 3 },
  { name: "Sent", value: 6 },
  { name: "Quote received", value: 8 },
  { name: "Closed", value: 2 },
];
const COLORS = ["#94a3b8", "#38bdf8", "#10b981", "#d4a24c"];

const AXIS_STYLE = { fontSize: 11, fill: "#94a3b8" };
const TOOLTIP_STYLE = {
  backgroundColor: "rgba(10,22,40,0.95)",
  border: "1px solid rgba(255,255,255,0.1)",
  borderRadius: 6, color: "#e2e8f0", fontSize: 12,
};

function Card({ title, subtitle, children, testId }) {
  return (
    <div className="rounded-md border border-white/10 bg-[hsl(var(--navy-muted))]/70 p-5" data-testid={testId}>
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="font-semibold tracking-tight text-slate-100 text-[15px]">{title}</div>
          {subtitle && <div className="text-[11px] text-slate-500 mt-0.5">{subtitle}</div>}
        </div>
        <span className="pill border-amber-500/40 bg-amber-500/10 text-amber-400">Demo</span>
      </div>
      <div className="h-56">{children}</div>
    </div>
  );
}

export function DemoAnalytics() {
  return (
    <section id="analytics" className="border-t border-white/5 bg-[hsl(var(--navy-muted))]/30">
      <div className="max-w-7xl mx-auto px-6 py-20">
        <div className="flex flex-wrap items-end justify-between gap-5 mb-8">
          <div>
            <div className="eyebrow mb-3">Procurement intelligence</div>
            <h2 className="text-3xl md:text-4xl font-semibold tracking-tight leading-tight text-white">
              The analytics you&apos;ll see <span className="text-[hsl(var(--gold))]">once your sourcing is running.</span>
            </h2>
            <p className="text-sm text-slate-400 mt-4 max-w-xl leading-relaxed">
              Preview of the dashboards buyers get inside GlobalBuy AI. Numbers below are illustrative; your dashboard uses only your real stored data.
            </p>
          </div>
          <div className="rounded-md border border-amber-500/40 bg-amber-500/10 p-3 flex items-start gap-2 max-w-sm">
            <AlertTriangle size={16} className="text-amber-400 mt-0.5 shrink-0"/>
            <div className="text-[12px] text-amber-200 leading-relaxed">
              All numbers on this page are illustrative examples. Real analytics are computed from your own RFQs and quotations.
            </div>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-5">
          <Card title="Supplier quote comparison" subtitle="Price per quotation (USD)" testId="demo-chart-prices">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={QUOTE_COMPARE} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
                <CartesianGrid stroke="rgba(255,255,255,0.06)" vertical={false}/>
                <XAxis dataKey="supplier" tick={AXIS_STYLE} interval={0} angle={-18} textAnchor="end" height={56}/>
                <YAxis tick={AXIS_STYLE}/>
                <Tooltip contentStyle={TOOLTIP_STYLE} cursor={{ fill: "rgba(212,162,76,0.08)" }}/>
                <Bar dataKey="price" fill="#d4a24c" radius={[4,4,0,0]}/>
              </BarChart>
            </ResponsiveContainer>
          </Card>

          <Card title="Price vs lead time" subtitle="Lower-left is faster & cheaper" testId="demo-chart-scatter">
            <ResponsiveContainer width="100%" height="100%">
              <ScatterChart margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                <CartesianGrid stroke="rgba(255,255,255,0.06)"/>
                <XAxis type="number" dataKey="lead" name="Lead time (days)" tick={AXIS_STYLE} label={{ value: "Lead time (days)", position: "insideBottom", offset: -5, fill: "#64748b", fontSize: 10 }}/>
                <YAxis type="number" dataKey="price" name="Price (USD)" tick={AXIS_STYLE}/>
                <ZAxis range={[80, 120]}/>
                <Tooltip contentStyle={TOOLTIP_STYLE} cursor={{ strokeDasharray: "3 3" }} formatter={(v, n) => [v, n]}/>
                <Scatter name="Quotations" data={QUOTE_COMPARE} fill="#38bdf8"/>
              </ScatterChart>
            </ResponsiveContainer>
          </Card>

          <Card title="Supplier response activity" subtitle="Responses received per day" testId="demo-chart-activity">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={RESPONSE_ACTIVITY} margin={{ top: 5, right: 10, left: -15, bottom: 0 }}>
                <CartesianGrid stroke="rgba(255,255,255,0.06)" vertical={false}/>
                <XAxis dataKey="day" tick={AXIS_STYLE}/>
                <YAxis tick={AXIS_STYLE}/>
                <Tooltip contentStyle={TOOLTIP_STYLE}/>
                <Line type="monotone" dataKey="responses" stroke="#d4a24c" strokeWidth={2} dot={{ r: 3, fill: "#d4a24c" }} activeDot={{ r: 5 }}/>
              </LineChart>
            </ResponsiveContainer>
          </Card>

          <Card title="RFQ status distribution" subtitle="Current RFQs by state" testId="demo-chart-status">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={STATUS} dataKey="value" nameKey="name" innerRadius={48} outerRadius={78} paddingAngle={2}>
                  {STATUS.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]}/>)}
                </Pie>
                <Tooltip contentStyle={TOOLTIP_STYLE}/>
                <Legend wrapperStyle={{ fontSize: 11, color: "#94a3b8" }}/>
              </PieChart>
            </ResponsiveContainer>
          </Card>
        </div>
      </div>
    </section>
  );
}
